import { useCallback, useMemo, useState } from 'react';

import moment, { Moment } from 'moment';
import { noop } from 'lodash';
import {
	Button,
	DateRangePicker,
	DaysInWeek,
	Form,
	FormItem,
	InputNumber,
	Radio,
	Space,
	TimeRangePicker,
	useForm
} from '@bringg/react-components';
import { Notification } from '@bringg/react-components';
import { EndOfWeekDay } from '@bringg/types';
import { useHasFeatureFlag } from '@bringg-frontend/hooks';

import { useStores } from 'bringg-web/recipes';
import Dialog from '../../components/dialog/dialog';
import RemoveDialog from './remove-dialog';
import UpdateDialog from './update-dialog';
import { CalendarEvent } from '../data-utils';
import { isoWeekdayToEndOfWeekday } from '../utils/week-day';
import { useSpeedFactorTranslation } from '../translations';
import { SpeedFactorGroup } from '../speed-factor-grouping/types';
import { getFormIntervalsWithEventDate } from '../utils/generic';
import { formDataManager } from '../utils/form-data-manager';
import { useDeliveredState } from '../hooks';
import { type FormValues } from '../types';

import styles from './traffic-calendar.module.scss';

export const allWeek = [
	EndOfWeekDay.Sunday,
	EndOfWeekDay.Monday,
	EndOfWeekDay.Tuesday,
	EndOfWeekDay.Wednesday,
	EndOfWeekDay.Friday,
	EndOfWeekDay.Saturday,
	EndOfWeekDay.Thursday
];

const timeFormat = 'HH:mm';

const isAllDay = (interval_start: number, interval_end: number, timezone = 'UTC') => {
	const anchorDate = moment().tz(timezone);
	const timezoneOffset = anchorDate.utcOffset() / 60 - Number(anchorDate.isDST());

	if (timezoneOffset === 0) {
		return interval_start === 0 && interval_end === 24;
	}

	if (interval_start !== interval_end) {
		return false;
	}

	return timezoneOffset > 0 ? interval_start + timezoneOffset === 24 : interval_start + timezoneOffset === 0;
};

const getTimeFrame = (event: CalendarEvent, timezone: string) => {
	if (!event) return 'specificHours';

	const { interval_start, interval_end } = event.originalEvent;

	if (isAllDay(interval_start, interval_end, timezone)) {
		return 'allDay';
	}

	return 'specificHours';
};

const currentDayOfWeek: EndOfWeekDay = isoWeekdayToEndOfWeekday(moment().isoWeekday());

// NOTE: this will be refactored more on the next iteration
const initForm = (event: CalendarEvent, timezone: string) => {
	// TODO: think about setting applicableDays from event if exists
	const applicableDays = [currentDayOfWeek];

	const effectiveDates: [Moment, Moment] = event
		? [moment(event.originalStart).tz(timezone), moment(event.originalEnd).tz(timezone)]
		: [moment.tz(timezone).startOf('year'), moment.tz(timezone).endOf('year')];

	let intervals: [Moment, Moment] = [
		moment.tz('6:00', timeFormat, timezone),
		moment.tz('9:00', timeFormat, timezone)
	];

	if (event) {
		const { start, end } = event;
		const momentEnd = moment(end).tz(timezone);

		// Intervals with event date
		intervals = [moment(start).tz(timezone), momentEnd.add(Number(momentEnd.minutes() === 59), 'minutes')];
	}

	const factor = event?.originalEvent ? (event.originalEvent.factor * 100 - 100).toFixed() : 0;

	const timeFrame = getTimeFrame(event, timezone);

	return {
		applicableDays,
		effectiveDates,
		intervals,
		factor,
		timeFrame
		// TODO: Probably ServiceAreaId could be a part of a form state (aka EventFormData type)
		// serviceAreaId
	};
};

type Props = {
	open: boolean;
	onSubmit: () => void;
	onCancel: () => void;
	event: CalendarEvent;
	groupedSpeedFactors: SpeedFactorGroup[];
	serviceAreaId: number;
	timezone: string;
};

const EventDialog = ({ open, onSubmit, onCancel, event, groupedSpeedFactors, serviceAreaId, timezone }: Props) => {
	const { merchantConfigurationsStore, serviceAreasStore } = useStores();
	const [daysInWeek, setDaysInWeek] = useState<EndOfWeekDay[]>([currentDayOfWeek]);

	const [timeFrame, setTimeFrame] = useState(getTimeFrame(event, timezone));
	const editable = !!event;

	const [loading, setLoading] = useState(false);
	const [openRemove, setOpenRemove] = useState(false);
	const [openUpdate, setOpenUpdate] = useState(false);

	const negativeSpeedFactorFFEnabled = useHasFeatureFlag('negative_speed_factor_value_enabled');

	const translations = useSpeedFactorTranslation();

	const [form] = useForm();
	const initialValues = useMemo<FormValues>(() => initForm(event, timezone), [event, timezone]);
	const { intervalsDisabled } = useDeliveredState(initialValues);

	const handleOk = useCallback(async () => {
		setLoading(true);

		try {
			const response = await serviceAreasStore.createSpeedFactors(
				// TODO: Probably ServiceAreaId could be a part of a form state
				{
					...formDataManager.normalizeCreateData({ formData: form.getFieldsValue(), timezone }),
					service_area_id: serviceAreaId
				},
				timezone
			);

			Notification.success(response.message);

			form.resetFields();
			onSubmit();
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);

			Notification.error(message);
		} finally {
			setLoading(false);
		}
	}, [serviceAreaId, serviceAreasStore, onSubmit, form, setLoading]);

	if (!open) return null;

	const handleTimeFrameChange = e => {
		setTimeFrame(e.target.value);
		form.setFieldValue('timeFrame', e.target.value);
	};

	const handleCancel = () => {
		form.resetFields();
		onCancel();
	};

	const handleRemove = () => {
		setOpenRemove(false);
		onSubmit();
	};

	const handleUpdate = () => {
		setOpenUpdate(false);
		onSubmit();
	};

	const closeRemove = () => {
		setOpenRemove(false);
	};

	const closeUpdate = () => {
		setOpenUpdate(false);
	};

	const speedFactorMinValue = negativeSpeedFactorFFEnabled ? -50 : 0;

	return (
		<Dialog
			title={translations.eventTitle}
			subtitle={translations.eventSubtitle}
			open={open}
			handleOk={() => handleOk()}
			handleCancel={handleCancel}
			okText={translations.eventOk}
			okButtonProps={{ disabled: loading }}
			footer={
				editable && (
					<Footer
						onCancel={handleCancel}
						onRemove={() => setOpenRemove(true)}
						onUpdate={() => setOpenUpdate(true)}
					/>
				)
			}
		>
			<Form layout="vertical" form={form} initialValues={initialValues}>
				<FormItem label={translations.eventEffectiveDatesLabel}>
					<LabelHint>{translations.eventEffectiveDatesDescription}</LabelHint>
					<FormItem name="effectiveDates">
						<DateRangePicker
							startDate={initialValues.effectiveDates[0]}
							endDate={initialValues.effectiveDates[1]}
							translations={translations}
							onChange={noop}
							allowClear={false}
							allowEmpty={[false, false]}
							disabled={editable}
						/>
					</FormItem>
				</FormItem>

				<FormItem label={translations.eventFactorLabel}>
					<LabelHint>{translations.eventFactorDescription}</LabelHint>
					<FormItem name="factor">
						<InputNumber
							min={speedFactorMinValue}
							max={200}
							formatter={value => `${value}%`}
							parser={value => value!.replace('%', '')}
							onChange={noop}
							style={{ width: 120 }}
						/>
					</FormItem>
				</FormItem>

				{!editable && (
					<FormItem label={translations.eventApplicableDaysLabel} name="applicableDays">
						<DaysInWeek
							endOfWeekDay={merchantConfigurationsStore.endOfWeekDay}
							onChange={weekDays => setDaysInWeek(weekDays)}
							value={daysInWeek}
							translates={translations}
						/>
					</FormItem>
				)}

				<FormItem label={translations.eventTimeFrameLabel} name="timeFrame">
					<Radio.Group value={timeFrame} onChange={handleTimeFrameChange}>
						<Space direction="vertical" size="small">
							<Radio value="allDay" style={{ fontWeight: 400 }}>
								{translations.eventTimeFrameFullDay}
							</Radio>
							<Radio value="specificHours" style={{ fontWeight: 400 }}>
								{translations.eventTimeFrameHours}
							</Radio>
						</Space>
					</Radio.Group>
				</FormItem>

				{timeFrame === 'specificHours' && (
					<FormItem
						name="intervals"
						style={{ marginLeft: '24px', marginTop: '12px' }}
						normalize={(value: [Moment, Moment]) =>
							event
								? getFormIntervalsWithEventDate({
										hoursInterval: [value[0].hours(), value[1].hours()],
										calendarEventInterval: [moment(event.start), moment(event.end)],
										timezone
								  })
								: value
						}
					>
						<TimeRangePicker
							disabled={intervalsDisabled}
							minuteStep={60}
							use12Hours={false}
							format={timeFormat}
							disabledTime={() => ({
								disabledMinutes: () => [0, 20, 40]
							})}
							hideDisabledOptions
							allowClear={false}
							placeholder={[translations.from, translations.to]}
						/>
					</FormItem>
				)}
			</Form>

			<RemoveDialog
				open={openRemove}
				onOk={handleRemove}
				onCancel={closeRemove}
				event={event}
				groupedSpeedFactors={groupedSpeedFactors}
				timezone={timezone}
			/>

			<UpdateDialog
				open={openUpdate}
				onOk={handleUpdate}
				onCancel={closeUpdate}
				event={event}
				values={form.getFieldsValue()}
				groupedSpeedFactors={groupedSpeedFactors}
				timezone={timezone}
			/>
		</Dialog>
	);
};

const Footer = ({ onRemove, onCancel, onUpdate }) => {
	const translations = useSpeedFactorTranslation();

	return (
		<Space style={{ display: 'flex', justifyContent: 'space-between' }}>
			<Button onClick={onRemove}>{translations.eventRemove}</Button>
			<div>
				<Button onClick={onCancel}>{translations.eventCancel}</Button>
				<Button type="primary" onClick={onUpdate}>
					{translations.eventUpdate}
				</Button>
			</div>
		</Space>
	);
};

const LabelHint = ({ children }) => {
	return <div className={styles.labelHint}>{children}</div>;
};

export default EventDialog;
